import React from 'react';

import {observer} from 'mobx-react';
import classNames from 'classnames';

import PhaseDone from '../../public/images/phaseDone.svg';
import styles from './CheckBox.module.scss';

interface CheckBoxProps {
    onChange: (values: boolean) => void;
    value: boolean;
    ariaLabel?: string | 'unlabeled';
    error?: string;
    label?: string;
    reversed?: boolean;
    disabled?: boolean;
    noCheckMark?: boolean;
    className?: string;
    noActionOnLabel?: boolean;
    children?: React.ReactChild;
    wrapperClassName?: string;
    labelClassName?: string;
}

const CheckBox: React.FC<CheckBoxProps> = ({
    value,
    onChange,
    label,
    reversed,
    disabled,
    error,
    ariaLabel,
    noCheckMark,
    children,
    className,
    noActionOnLabel,
    wrapperClassName,
    labelClassName,
}) => {
    const content = label || children || '';

    return (
        <div>
            <div
                tabIndex={1}
                aria-label={ariaLabel || label}
                className={classNames(styles.CheckBox, {[styles.disabled]: disabled}, wrapperClassName)}
                onClick={() => !disabled && !noActionOnLabel && onChange(!value)}
            >
                {reversed ? (
                    <>
                        <Label value={value} styles={styles} content={content} className={labelClassName} />
                        <Checkbox
                            className={className}
                            noCheckMark={noCheckMark}
                            value={value}
                            error={error}
                            onChange={onChange}
                        />
                    </>
                ) : (
                    <>
                        <Checkbox
                            className={className}
                            noCheckMark={noCheckMark}
                            value={value}
                            error={error}
                            onChange={onChange}
                        />
                        <Label value={value} styles={styles} content={content} className={labelClassName} />
                    </>
                )}
            </div>
            {!!error && <div className={styles.errorText}>{error}</div>}
        </div>
    );
};

export default observer(CheckBox);

type CheckBox = {
    value: boolean;
    error: string;
    noCheckMark: boolean;
    className?: string;
    onChange?: (values: boolean) => void;
};

const Checkbox: React.FC<CheckBox> = ({value = false, error, noCheckMark, className, onChange}) => {
    const handleOnClick = () => {
        onChange(!value);
    };

    return (
        <>
            <input onChange={handleOnClick} type="checkbox" value={`${value}`} />
            <div
                onClick={handleOnClick}
                className={[
                    styles.box,
                    value && styles.checked,
                    !!error && styles.error,
                    noCheckMark && styles.noCheckMark,
                    className && className,
                ].join(' ')}
            >
                {value && !noCheckMark && <PhaseDone />}
            </div>
        </>
    );
};

const Label = (props) => {
    const {value, styles, content, className} = props;

    return <div className={[styles.label, value && styles.checkedLabel, className].join(' ')}>{content}</div>;
};
